import { useState, useEffect, useMemo, useCallback } from "react";
import styles from "./Header.module.scss";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  HiMenuAlt2,
  HiX,
  HiClipboardList,
  HiOutlineClipboardCheck,
  HiOutlineDocumentReport,
  HiOutlineAcademicCap,
  HiOutlineChartBar,
  HiOutlineCog,
  HiOutlineCloudUpload,
  HiOutlinePhotograph,
  HiOutlineMap,
  HiOutlineCollection,
  HiOutlineDotsHorizontal,
} from "react-icons/hi";
import { MdBusinessCenter } from "react-icons/md";
import {
  Tooltip,
  Box,
  MenuItem,
  Menu,
  Typography,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import logo from "../../../assets/logo.png";
import directoryIcon from "../../../assets/directory.png";
import auditsIcon from "../../../assets/audits.png";
import analyticsIcon from "../../../assets/Analytics.png";
import trainingsManagementIcon from "../../../assets/trainings-management.png";
import trainingsProgressIcon from "../../../assets/trainings-progress.png";
import changePasswordIcon from "../../../assets/change-password.png";
import logoutIcon from "../../../assets/logout.png";
import { useOrganization } from "../../../contexts/OrganizationContext";
import { useOrganizationDynamicProperties } from "../../../api/hooks/useOrganizationDynamicProperties";
import { usePreloadDynamicPropertyTypes } from "../../../api/hooks/useDynamicPropertyTypes";
import OrgLogo from "./OrgLogo";
import { useAuth } from "../../../contexts/AuthContext";
import { supabase } from "../../../utils/supabaseClient";
import { useAuthorization } from "../../../contexts/AuthorizationContext";
import { NAV_ENTRIES, NavItem, isGroup } from "../../../config/navigation";

const ICON_SIZE = 20;

const iconRenderers: Record<string, React.ReactNode> = {
  directory: <img src={directoryIcon} alt="Directory" className={styles.navIcon} />,
  audits: <img src={auditsIcon} alt="Auditorias" className={styles.navIcon} />,
  analytics: <img src={analyticsIcon} alt="Analytics" className={styles.navIcon} />,
  trainingsManagement: <img src={trainingsManagementIcon} alt="Trainings" className={styles.navIcon} />,
  trainingsProgress: <img src={trainingsProgressIcon} alt="Trainings" className={styles.navIcon} />,
  challenges: <HiOutlineClipboardCheck size={ICON_SIZE} />,
  challengesManage: <HiOutlineCollection size={ICON_SIZE} />,
  reports: <HiOutlineDocumentReport size={ICON_SIZE} />,
  reportBuilder: <HiOutlineDocumentReport size={ICON_SIZE} />,
  programs: <HiOutlineCog size={ICON_SIZE} />,
  sync: <HiOutlineCloudUpload size={ICON_SIZE} />,
  userUploader: <HiOutlineCloudUpload size={ICON_SIZE} />,
  logoUploader: <HiOutlinePhotograph size={ICON_SIZE} />,
  logosFromMap: <HiOutlineMap size={ICON_SIZE} />,
  activityLog: <HiClipboardList size={ICON_SIZE} />,
  management: <HiOutlineCog size={ICON_SIZE} />,
  reportsSync: <HiOutlineDocumentReport size={ICON_SIZE} />,
  trainingsAcademic: <HiOutlineAcademicCap size={ICON_SIZE} />,
  analyticsChart: <HiOutlineChartBar size={ICON_SIZE} />,
  more: <HiOutlineDotsHorizontal size={ICON_SIZE} />,
};

export const Header = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();
  const { ownOrganizations, selectedOrganization, changeSelectedOrganization } =
    useOrganization();
  const { hasPrivilege, loading: authLoading } = useAuthorization();

  // Fetch dynamic properties for conditional navigation
  const { data: dynamicPropertyTypes } = usePreloadDynamicPropertyTypes();
  const { data: organizationDynamicProperties } =
    useOrganizationDynamicProperties(selectedOrganization?.id || "");

  // Menu state: keyed by group label, value is the anchor element
  const [groupMenuAnchor, setGroupMenuAnchor] = useState<Record<string, HTMLElement | null>>({});
  // Org switcher menu
  const [orgMenuAnchor, setOrgMenuAnchor] = useState<null | HTMLElement>(null);
  // Logo (account) menu
  const [logoMenuAnchor, setLogoMenuAnchor] = useState<null | HTMLElement>(null);

  // Check if we're in admin route
  const isAdminRoute = location.pathname.startsWith("/admin-v2") || location.pathname.startsWith("/admin");

  const handleOpenOrgMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOrgMenuAnchor(event.currentTarget);
  };

  const handleCloseOrgMenu = () => {
    setOrgMenuAnchor(null);
  };

  const handleOrgChange = (orgId: string) => {
    changeSelectedOrganization(orgId);
    handleCloseOrgMenu();
  };

  const handleOpenGroupMenu = (label: string) => (event: React.MouseEvent<HTMLElement>) => {
    setGroupMenuAnchor((prev) => ({ ...prev, [label]: event.currentTarget }));
  };

  const handleCloseGroupMenu = (label: string) => () => {
    setGroupMenuAnchor((prev) => ({ ...prev, [label]: null }));
  };

  // Helper: check a dynamic property (org-specific -> global default -> deny)
  const getDynamicPropertyVisibility = useCallback((propertyName: string): boolean => {
    if (organizationDynamicProperties?.data) {
      const prop = organizationDynamicProperties.data.find(
        (p) => p.propertyTypeName === propertyName,
      );
      if (prop?.value) return prop.value === "1";
    }
    if (dynamicPropertyTypes?.data) {
      const type = dynamicPropertyTypes.data.find(
        (t) => t.name === propertyName,
      );
      if (type?.defaultValue) return type.defaultValue === "1";
    }
    return false;
  }, [organizationDynamicProperties, dynamicPropertyTypes]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setIsOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const defaultSelectedItem = (path: string) =>
    location.pathname === path && styles.defaultSelectedTitle;

  const handleLinkClick = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  // Check if a single nav item should be visible
  const itemIsVisible = useCallback(
    (item: NavItem): boolean => {
      if (item.privilege && !hasPrivilege(item.privilege)) return false;
      if (item.dynamicProperty && !getDynamicPropertyVisibility(item.dynamicProperty))
        return false;
      return true;
    },
    [hasPrivilege, getDynamicPropertyVisibility],
  );

  // Filter nav entries: groups are shown if at least one child is visible
  const visibleEntries = useMemo(() => {
    if (authLoading) return [];
    return NAV_ENTRIES.filter((entry) => {
      if (isGroup(entry)) {
        return entry.children.some(itemIsVisible);
      }
      return itemIsVisible(entry);
    }).map((entry) => {
      if (isGroup(entry)) {
        return { ...entry, children: entry.children.filter(itemIsVisible) };
      }
      return entry;
    });
  }, [authLoading, itemIsVisible]);

  const renderIcon = (iconKey: string, label: string) =>
    iconRenderers[iconKey] ?? <span className={styles.letterIcon}>{label[0]}</span>;

  const showUpdatePassword = user && user.email;
  const showOrgSwitcher = !isAdminRoute && ownOrganizations.length > 1;

  return (
    <div
      className={`${styles.container} ${isOpen ? styles.open : styles.closed}`}
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <button
        className={`${styles.burgerButton} ${isOpen ? styles.open : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <HiX size={24} /> : <HiMenuAlt2 size={24} />}
      </button>

      <Tooltip title="Opciones de cuenta" placement="right" arrow>
        <div
          className={styles.logo}
          style={{ flexShrink: 0, margin: "8px 0", cursor: "pointer" }}
          onClick={(e) => setLogoMenuAnchor(e.currentTarget)}
        >
          <img src={logo} alt="Logo" className={styles.logoImage} />
        </div>
      </Tooltip>

      {/* Org logo: clickable to switch organization if multiple */}
      <Tooltip
        title={showOrgSwitcher ? "Cambiar organizacion" : selectedOrganization?.name || ""}
        placement="right"
        arrow
      >
        <div
          style={{ flexShrink: 0, cursor: showOrgSwitcher ? "pointer" : "default" }}
          onClick={showOrgSwitcher ? handleOpenOrgMenu : undefined}
          aria-controls={showOrgSwitcher && orgMenuAnchor ? "org-menu" : undefined}
          aria-haspopup={showOrgSwitcher ? "true" : undefined}
          aria-expanded={showOrgSwitcher && orgMenuAnchor ? "true" : undefined}
        >
          <OrgLogo organization={selectedOrganization} />
        </div>
      </Tooltip>

      <nav
        className={styles.tabHeader}
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          marginTop: "24px",
          gap: "8px",
          paddingBottom: "8px",
        }}
      >
        {visibleEntries.map((entry) => {
          if (isGroup(entry)) {
            const menuOpen = Boolean(groupMenuAnchor[entry.label]);
            return (
              <div key={entry.label}>
                <Tooltip title={entry.label} placement="right" arrow>
                  <div
                    className={styles.tabHeaderIcon}
                    onClick={handleOpenGroupMenu(entry.label)}
                    style={{ cursor: "pointer" }}
                  >
                    {renderIcon(entry.iconKey, entry.label)}
                  </div>
                </Tooltip>
                <Menu
                  anchorEl={groupMenuAnchor[entry.label]}
                  open={menuOpen}
                  onClose={handleCloseGroupMenu(entry.label)}
                  PaperProps={{ style: { maxHeight: 300, width: 250 } }}
                >
                  {entry.children.map((child) => (
                    <MenuItem
                      key={child.path}
                      onClick={() => {
                        handleCloseGroupMenu(entry.label)();
                        navigate(child.path);
                        handleLinkClick();
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {renderIcon(child.iconKey, child.label)}
                      </ListItemIcon>
                      <ListItemText>{child.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            );
          }

          return (
            <Tooltip key={entry.path} title={entry.label} placement="right" arrow>
              <Link
                to={entry.path}
                className={`${styles.tabHeaderIcon} ${defaultSelectedItem(entry.path)}`}
                onClick={handleLinkClick}
              >
                {renderIcon(entry.iconKey, entry.label)}
              </Link>
            </Tooltip>
          );
        })}
      </nav>

      {/* Account Menu (triggered by Papo logo) */}
      <Menu
        anchorEl={logoMenuAnchor}
        open={Boolean(logoMenuAnchor)}
        onClose={() => setLogoMenuAnchor(null)}
        PaperProps={{ style: { width: 220 } }}
      >
        {showUpdatePassword && user && (
          <MenuItem
            onClick={() => {
              setLogoMenuAnchor(null);
              navigate("/update-password");
              handleLinkClick();
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <img src={changePasswordIcon} alt="" style={{ width: 20, height: 20 }} />
            </ListItemIcon>
            <ListItemText>Cambiar Contraseña</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          onClick={() => {
            setLogoMenuAnchor(null);
            supabase.auth.signOut();
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <img src={logoutIcon} alt="" style={{ width: 20, height: 20 }} />
          </ListItemIcon>
          <ListItemText>Cerrar Sesión</ListItemText>
        </MenuItem>
      </Menu>

      {/* Organization Switcher Menu */}
      <Menu
        id="org-menu"
        anchorEl={orgMenuAnchor}
        open={Boolean(orgMenuAnchor)}
        onClose={handleCloseOrgMenu}
        MenuListProps={{ "aria-labelledby": "org-menu-button" }}
        PaperProps={{ style: { maxHeight: 300, width: 250 } }}
      >
        {ownOrganizations.map((org) => (
          <MenuItem
            key={org.id}
            onClick={() => handleOrgChange(org.id)}
            selected={selectedOrganization?.id === org.id}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MdBusinessCenter size={16} />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {org.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {org.isMain ? "Principal" : org.isFarm ? "Finca" : "Regular"}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};
